import React, { useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Chip, Switch, FormControlLabel, Alert, List, ListItem, ListItemText, CircularProgress, Tooltip,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import providerExamService from '../../../services/providerExamService';
import { ProviderExamImportPreview } from '../../../types/providerExam';
import { isApiError } from '../../../types/common';
import { useAcao } from '../../../authorization/useVeredito';

interface Props {
  open: boolean;
  providerId: number;
  onClose: () => void;
  onImported: () => void; // recarrega a lista
}

const ProviderExamsImportDialog: React.FC<Props> = ({ open, providerId, onClose, onImported }) => {
  const { enqueueSnackbar } = useSnackbar();
  const apagarAusentes = useAcao('provider_exam.delete', 'Apagar vínculos ausentes da planilha');
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ProviderExamImportPreview | null>(null);
  const [deleteMissing, setDeleteMissing] = useState(false);
  const [loading, setLoading] = useState(false);

  const reset = () => { setFile(null); setPreview(null); setDeleteMissing(false); setLoading(false); };
  const handleClose = () => { reset(); onClose(); };

  const handleFile = async (f: File) => {
    setFile(f); setLoading(true);
    try {
      const p = await providerExamService.previewImport(providerId, f);
      setPreview(p);
    } catch (err: unknown) {
      const msg = isApiError(err) ? err.message : err instanceof Error ? err.message : 'Erro ao analisar arquivo';
      enqueueSnackbar(msg, { variant: 'error' });
      reset();
    } finally { setLoading(false); }
  };

  const handleConfirm = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const res = await providerExamService.commitImport(providerId, file, deleteMissing && !apagarAusentes.disabled);
      enqueueSnackbar(`Importado: ${res.created} criados, ${res.updated} atualizados, ${res.deleted} removidos, ${res.skipped} com erro`, { variant: res.skipped > 0 ? 'warning' : 'success' });
      onImported();
      handleClose();
    } catch (err: unknown) {
      const msg = isApiError(err) ? err.message : err instanceof Error ? err.message : 'Erro ao importar';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={() => { if (!loading) handleClose(); }} disableEscapeKeyDown={loading} maxWidth="sm" fullWidth>
      <DialogTitle>Importar exames (vínculo)</DialogTitle>
      <DialogContent dividers>
        {!preview && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <input
              ref={inputRef} type="file" accept=".xlsx,.xls" hidden
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Envie uma planilha (mesmo formato do export). A chave de importação é o <b>Código eSocial</b>.
            </Typography>
            <Button variant="outlined" onClick={() => inputRef.current?.click()} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Selecionar arquivo'}
            </Button>
          </Box>
        )}

        {preview && (
          <Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip color="success" label={`Criar: ${preview.toCreate}`} />
              <Chip color="info" label={`Atualizar: ${preview.toUpdate}`} />
              <Chip color="warning" label={`Remover: ${preview.toDeleteCount}`} />
              <Chip color={preview.errors.length ? 'error' : 'default'} label={`Erros: ${preview.errors.length}`} />
            </Box>

            <Tooltip title={apagarAusentes.tooltip}>
              <FormControlLabel
                control={
                  <Switch
                    checked={deleteMissing}
                    disabled={apagarAusentes.disabled}
                    onChange={(e) => setDeleteMissing(e.target.checked)}
                  />
                }
                label={`Apagar vínculos ausentes da planilha${deleteMissing ? ` (${preview.toDeleteCount})` : ''}`}
              />
            </Tooltip>
            {deleteMissing && preview.toDeleteCount > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {preview.toDeleteCount} vínculo(s) serão removidos permanentemente.
              </Alert>
            )}

            {preview.errors.length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Linhas com erro (serão ignoradas):</Typography>
                <List dense>
                  {preview.errors.slice(0, 50).map((e, i) => (
                    <ListItem key={i} disablePadding>
                      <ListItemText primary={`${e.row > 0 ? `Linha ${e.row}` : 'Arquivo'}${e.esocialCode ? ` (${e.esocialCode})` : ''}: ${e.reason}`} />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancelar</Button>
        {preview && <Button onClick={reset} disabled={loading}>Trocar arquivo</Button>}
        {preview && (
          <Button variant="contained" onClick={handleConfirm} disabled={loading || (preview.toCreate + preview.toUpdate + (deleteMissing ? preview.toDeleteCount : 0) === 0)}>
            {loading ? <CircularProgress size={20} /> : 'Confirmar importação'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProviderExamsImportDialog;
